/**
 * The wizard's phone preview borrows the admin page's session — it never
 * refreshes on its own (the user, 2026-10-13: "it logs me out when accessing
 * the wizard").
 *
 * Why: `/auth/refresh` ROTATES the refresh cookie, and the preview and the page
 * share it. The preview reloads after every save (`WizardPreview`); a reload
 * that cut its refresh off left the server's new cookie unreceived, the page's
 * next refresh presented the old one — a reuse — and the server ended the whole
 * sign-in. One window using the cookie is the cure: the page refreshes, the
 * frame ASKS it.
 *
 * The frame posts `{ type: 'myrave:session?' }` to its parent; the parent
 * answers `{ type: 'myrave:session', session }` to that frame alone (its own
 * `contentWindow`, the frame's exact origin), and only to a frame on a
 * subdomain of the platform. The frame takes an answer only from its parent,
 * and only from the rave admin's or the console's host. A token crosses
 * between two of our own windows, never anywhere else.
 */
import { platformDomain } from './tenant';

const ASK = 'myrave:session?';
const ANSWER = 'myrave:session';
/** No answer from the page in this long: the frame is told nothing (a network-like failure). */
const ASK_TIMEOUT_MS = 6_000;

interface FramedSession {
  accessToken: string;
  user: { id: string; email: string | null; role: string; displayName: string | null; approved: boolean; avatarUrl: string | null };
}

/** A host of this platform — `<sub>.<domain>` (a port allowed, http on localhost only). */
function platformHost(origin: string, sub?: RegExp): boolean {
  let u: URL;
  try { u = new URL(origin); } catch { return false; }
  const domain = platformDomain();
  if (u.protocol !== 'https:' && !(u.protocol === 'http:' && /localhost$/.test(domain))) return false;
  if (!u.hostname.endsWith(`.${domain}`)) return false;
  const label = u.hostname.slice(0, -(domain.length + 1));
  return !label.includes('.') && (!sub || sub.test(label));
}

/** The page the preview may take a session from: the rave admin, or the console. */
export function isAdminOrigin(origin: string): boolean {
  return platformHost(origin, /^(radm|appadm)$/);
}

/** A frame the page may hand its session to: a rave's own host. */
export function isRaveOrigin(origin: string): boolean {
  return platformHost(origin) && !isAdminOrigin(origin);
}

/** The frame's side: ask the page for its session. Rejects when the page does not answer. */
export function askParentSession(timeoutMs = ASK_TIMEOUT_MS): Promise<FramedSession | null> {
  return new Promise((resolve, reject) => {
    const parent = window.parent;
    const timer = setTimeout(() => { window.removeEventListener('message', heard); reject(new Error('the page did not answer')); }, timeoutMs);
    function heard(e: MessageEvent) {
      if (e.source !== parent || !isAdminOrigin(e.origin)) return;
      const data = e.data as { type?: unknown; session?: FramedSession | null } | null;
      if (!data || data.type !== ANSWER) return;
      clearTimeout(timer);
      window.removeEventListener('message', heard);
      resolve(data.session ?? null);
    }
    window.addEventListener('message', heard);
    // The request carries nothing secret; the answer goes back to this frame's origin only.
    parent.postMessage({ type: ASK }, '*');
  });
}

/**
 * The page's side: answer the frame's asks with the page's own session —
 * `current` refreshes it first when it is about to expire. Returns the
 * listener's removal.
 */
export function serveFramedSession(frame: () => HTMLIFrameElement | null, current: () => Promise<FramedSession | null>): () => void {
  const asked = async (e: MessageEvent) => {
    const el = frame();
    if (!el || e.source !== el.contentWindow || !isRaveOrigin(e.origin)) return;
    if ((e.data as { type?: unknown } | null)?.type !== ASK) return;
    let session: FramedSession | null = null;
    try { session = await current(); } catch { session = null; }
    const s = session ? { accessToken: session.accessToken, user: session.user } : null;
    el.contentWindow?.postMessage({ type: ANSWER, session: s }, e.origin);
  };
  window.addEventListener('message', asked);
  return () => window.removeEventListener('message', asked);
}
